/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import * as loginActions from "../../../../store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "@/src/services/landingPagesList";
import Typography from "@mui/material/Typography";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { Box } from "@mui/material";
import type { PartnerIri } from "@/src/constants/partnerIris";
import { getPartnerNameByIri } from "@/src/constants/partnerIris";
import CheckInFormPerson from "../../Common/CheckInFormPerson.tsx";
import basePersonConsultingCheckinStyle from "./basePersonConsultingCheckinStyle.ts";
import { FormDataTypes } from "@/src/types/FormDataTypes";
import { AbstractCase, productClassNames } from "@/src/types/Entities/AbstractCase";

type BasePersonConsultingCheckinProps = {
  idPrefix: string;
  color?: "default" | "grey";
  target?: string;
  partner: PartnerIri;
  getApiProductUri: (selectedProduct: AbstractCase["name"]) => string;
  getInitialProduct: (selectedProduct: AbstractCase["name"]) => string;
  additionalInitialValues?: FormDataTypes["initialValues"];
  renderAdditionalFields?: (isLoading: boolean) => React.ReactNode;
  preSubmitAction?: (values: FormDataTypes["values"]) => Promise<string | null>;
};

export default function BasePersonConsultingCheckin({
  color = "default",
  target = "/servicewelt",
  idPrefix,
  partner,
  getApiProductUri,
  getInitialProduct,
  additionalInitialValues = {},
  renderAdditionalFields,
  preSubmitAction,
}: BasePersonConsultingCheckinProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const [customSubmitErrorText, setCustomSubmitErrorText] = useState("");

  const initialValues = {
    telephone: "",
    email: "",
    gender: "",
    familyName: "",
    givenName: "",
    product: "",
    insurancePolicyNumber: "",
    ...additionalInitialValues,
  };

  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const handleSubmit = async ({ values }: { values: FormDataTypes["values"] }) => {
    setIsLoading(true);
    setCustomSubmitErrorText("");

    const validProduct = productClassNames.find((product) => product === values.product);
    if (!validProduct) {
      setCustomSubmitErrorText("Es wurde ein ungültiges Produkt ausgewählt.");
      setIsLoading(false);
      return;
    }

    if (preSubmitAction) {
      const errorMessage = await preSubmitAction(values);
      if (errorMessage) {
        setCustomSubmitErrorText(errorMessage);
        setIsLoading(false);
        return;
      }
    }

    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email.toLowerCase());
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);

    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      telephone: values.telephone,
      person: {
        gender: values.gender,
        givenName: values.givenName,
        familyName: values.familyName,
      },
      prelead: {
        initialProduct: getInitialProduct(values.product),
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    await loginActions.register(data);

    await createMutation.mutateAsync({
      uri: getApiProductUri(values.product),
      data: {
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        landingPage: landingPage.keyword,
        caseInsurance: {
          insurancePolicyNumber: values.insurancePolicyNumber,
        },
      },
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={[basePersonConsultingCheckinStyle.formPaper, basePersonConsultingCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} sx={basePersonConsultingCheckinStyle.scrollToAnchor} />
      <Typography variant={"h5"} sx={basePersonConsultingCheckinStyle.heading}>
        Jetzt professionelle Erstberatung vereinbaren
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <CheckInFormPerson
          isLoading={isLoading}
          emailErrorText={emailErrorText}
          setEmailErrorText={setEmailErrorText}
          additionalFormFields={renderAdditionalFields && renderAdditionalFields(isLoading)}
          submitHintText={customSubmitErrorText}
        />
      </FormProvider>
    </Paper>
  );
}
